// SignupForm — registration form modal with date/time pulled from the active
// event (no hardcoded month strings). Replaces RegistrationModal at the import
// layer; named differently because src/components/RegistrationModal.tsx
// inherited a Dropbox-era directory-entry size cap.

import { useState, useEffect, useRef } from 'react';
import { X, Loader2, CheckCircle2 } from 'lucide-react';
import { api, type PublicEvent } from '../api';

function formatEventBlurb(e: PublicEvent): string {
  const s = new Date(e.start_date);
  const en = new Date(e.end_date);
  const fmtDay = (d: Date) =>
    d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
  const fmtTime = (d: Date) =>
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' })
      .replace(' AM', 'am').replace(' PM', 'pm');
  const year = s.getUTCFullYear();
  const sameDay =
    s.getUTCFullYear() === en.getUTCFullYear() &&
    s.getUTCMonth() === en.getUTCMonth() &&
    s.getUTCDate() === en.getUTCDate();
  const dayPart = sameDay ? fmtDay(s) : `${fmtDay(s)} & ${fmtDay(en)}`;
  return `${dayPart}, ${year} · ${fmtTime(s)}–${fmtTime(en)}`;
}

function formatFriendlyRange(e: PublicEvent): string {
  const s = new Date(e.start_date);
  const en = new Date(e.end_date);
  const month = s.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
  const sameMonth =
    s.getUTCMonth() === en.getUTCMonth() && s.getUTCFullYear() === en.getUTCFullYear();
  if (sameMonth) {
    return `${month} ${s.getUTCDate()}–${en.getUTCDate()}, ${s.getUTCFullYear()}`;
  }
  const endMonth = en.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
  return `${month} ${s.getUTCDate()} – ${endMonth} ${en.getUTCDate()}, ${s.getUTCFullYear()}`;
}

interface SignupFormProps {
  event: PublicEvent;
  open: boolean;
  onClose: () => void;
}

interface FormState {
  full_name: string;
  email: string;
  phone: string;
  attendee_count: string;
  special_requests: string;
  // Task #50: when attendee_count > 1, capture each additional attendee's
  // name. guest_names[0] is the 2nd seat, guest_names[1] is the 3rd, etc.
  guest_names: string[];
  // Task #15: required for hybrid events. Defaults to in_person for hybrid;
  // ignored by the backend for pure in_person / online events.
  attendance_mode: 'in_person' | 'online';
}

const EMPTY_FORM: FormState = {
  full_name: '',
  email: '',
  phone: '',
  attendee_count: '1',
  special_requests: '',
  guest_names: [],
  attendance_mode: 'in_person',
};

export function SignupForm({ event, open, onClose }: SignupFormProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [successStatus, setSuccessStatus] = useState<'registered' | 'waitlisted' | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM);
      setError('');
      setSuccessStatus(null);
      setSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const update = <K extends keyof FormState>(k: K) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [k]: e.target.value }));
    };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.full_name.trim() || !form.email.trim()) {
      setError('Please fill in your name and email.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const seats = Math.max(1, parseInt(form.attendee_count, 10) || 1);
      // Trim + keep only the first (seats - 1) guest names. The backend
      // truncates extras too, but trimming client-side keeps payload tidy.
      const guestNames = form.guest_names
        .slice(0, Math.max(0, seats - 1))
        .map((n) => n.trim())
        .filter((n) => n.length > 0);
      // Task #15: coerce mode based on event format. Pure formats override
      // the form's choice; hybrid honors it.
      const effectiveMode =
        event.format === 'online' ? 'online'
        : event.format === 'in_person' ? 'in_person'
        : form.attendance_mode;
      const result = await api.register({
        event_id: event.event_id,
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        attendee_count: seats,
        special_requests: form.special_requests.trim() || undefined,
        guest_names: guestNames.length > 0 ? guestNames : undefined,
        attendance_mode: effectiveMode,
      });

      if (result.status === 'registered' || result.status === 'waitlisted') {
        setSuccessStatus(result.status);
      } else if (result.status === 'duplicate') {
        setError('It looks like you’ve already registered with this email. Check your inbox for confirmation, or contact support@upinfarms.com.');
      } else if (result.status === 'closed') {
        setError('Sorry — registration is now closed for this workshop.');
      } else {
        setError('Something unexpected happened. Please try again or contact support@upinfarms.com.');
      }
    } catch (err: any) {
      setError(err?.message || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="registration-modal-title"
    >
      <div className="fixed inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />

      <div
        ref={dialogRef}
        className="relative bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 id="registration-modal-title" className="text-lg font-semibold text-uif-green-dark">
            {successStatus ? 'You’re registered' : 'Register for the workshop'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {successStatus ? (
          <div className="px-5 py-8 text-center space-y-4">
            <CheckCircle2 className="h-12 w-12 text-uif-green mx-auto" />
            <h3 className="text-xl font-semibold text-gray-900">
              {successStatus === 'registered' ? 'Thanks for registering!' : 'You’re on the waitlist'}
            </h3>
            <p className="text-gray-700 leading-relaxed">
              {successStatus === 'registered'
                ? `We’ve recorded your seat for the SmartFarmed Food Safety Workshop on ${formatFriendlyRange(event)} in Jackson, MS. Watch your inbox — we’ll send confirmation details and a reminder closer to the date.`
                : 'The workshop is currently full, but you’re on the waitlist. We’ll email you immediately if a seat opens up.'}
            </p>
            <p className="text-sm text-gray-500">
              Questions? <a href="mailto:support@upinfarms.com" className="text-uif-green hover:underline">support@upinfarms.com</a>
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-2 inline-flex items-center justify-center rounded-md bg-uif-green text-white font-semibold px-6 py-2 hover:bg-uif-green-dark"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
            <div className="bg-uif-green/5 border border-uif-green/20 rounded-md px-3 py-2 text-sm text-gray-700">
              <div className="font-semibold text-uif-green-dark">{event.title}</div>
              <div>{formatEventBlurb(event)}</div>
              <div>{event.location ?? '2093 Lakeland Dr, Jackson, MS 39216'}</div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">
                {error}
              </div>
            )}

            <Field label="Full name *">
              <input
                type="text"
                required
                value={form.full_name}
                onChange={update('full_name')}
                disabled={submitting}
                autoComplete="name"
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-uif-green focus:ring-1 focus:outline-none"
              />
            </Field>

            <Field label="Email *">
              <input
                type="email"
                required
                value={form.email}
                onChange={update('email')}
                disabled={submitting}
                autoComplete="email"
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-uif-green focus:ring-1 focus:outline-none"
              />
            </Field>

            {/* Task #15: hybrid event mode picker. Required choice. */}
            {event.format === 'hybrid' && (
              <Field label="How will you attend?">
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'in_person' as const, label: 'In person', desc: 'Attend at the venue' },
                    { value: 'online'    as const, label: 'Online',    desc: 'Join the virtual meeting' },
                  ].map((opt) => (
                    <label
                      key={opt.value}
                      className={
                        'flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer ' +
                        (form.attendance_mode === opt.value
                          ? 'bg-uif-green/10 border-uif-green'
                          : 'bg-white border-gray-300 hover:border-uif-green')
                      }
                    >
                      <input
                        type="radio"
                        name="attendance_mode"
                        value={opt.value}
                        checked={form.attendance_mode === opt.value}
                        onChange={() => setForm((p) => ({ ...p, attendance_mode: opt.value }))}
                        disabled={submitting}
                        className="mt-0.5"
                      />
                      <div>
                        <div className="text-sm font-semibold text-gray-800">{opt.label}</div>
                        <div className="text-[11px] text-gray-500">{opt.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </Field>
            )}
            {/* Online-only events: show informational note instead of asking. */}
            {event.format === 'online' && (
              <div className="bg-uif-green/5 border border-uif-green/30 rounded-md px-3 py-2 text-sm text-uif-green-dark">
                🎥 <strong>This is an online event.</strong> You'll receive a meeting link in your confirmation email.
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone">
                <input
                  type="tel"
                  value={form.phone}
                  onChange={update('phone')}
                  disabled={submitting}
                  autoComplete="tel"
                  placeholder="(optional)"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-uif-green focus:ring-1 focus:outline-none"
                />
              </Field>

              <Field label="People attending">
                {/* Wave 2 item 2 (Task #45, R4=B): was <input type="number">, which
                    defaulted to "1" and silently clamped. Clicking the field put the
                    cursor after the 1, so typing "2" to replace produced "12" -> mutely
                    clamped to 10. Both real-world 10-seat accidents match that exact
                    signature. A <select> cannot produce an out-of-range value at all.
                    The onChange body below is unchanged — it still owns guest-name
                    padding/trimming, and the clamp stays as defence in depth. */}
                <select
                  value={form.attendee_count}
                  onChange={(e) => {
                    const newCount = Math.max(1, Math.min(10, parseInt(e.target.value, 10) || 1));
                    setForm((prev) => {
                      const extras = Math.max(0, newCount - 1);
                      const nextGuests = [...prev.guest_names];
                      // Pad with empty strings or trim if seats changed.
                      while (nextGuests.length < extras) nextGuests.push('');
                      while (nextGuests.length > extras) nextGuests.pop();
                      return { ...prev, attendee_count: String(newCount), guest_names: nextGuests };
                    });
                  }}
                  disabled={submitting}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-uif-green focus:ring-1 focus:outline-none"
                >
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={String(n)}>{n}</option>
                  ))}
                </select>
              </Field>
            </div>

            {/* Task #50: dynamic name fields when seats > 1. The primary
                registrant's name is captured in the first Field above; these
                are for the +1, +2, etc. */}
            {form.guest_names.length > 0 && (
              <div className="bg-uif-green/5 border border-uif-green/30 rounded-md p-3 space-y-2">
                <p className="text-xs font-semibold text-uif-green-dark uppercase tracking-wider">
                  Additional attendees
                </p>
                <p className="text-[11px] text-gray-600">
                  Please share each guest's name so we can prep materials and check them in at the door.
                </p>
                {form.guest_names.map((name, i) => (
                  <label key={i} className="block">
                    <span className="block text-xs text-gray-700 mb-0.5">Guest {i + 2}</span>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => {
                        const v = e.target.value;
                        setForm((prev) => {
                          const next = [...prev.guest_names];
                          next[i] = v;
                          return { ...prev, guest_names: next };
                        });
                      }}
                      disabled={submitting}
                      placeholder="Full name"
                      className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-uif-green focus:ring-1 focus:outline-none"
                    />
                  </label>
                ))}
              </div>
            )}

            <Field label="Anything we should know?">
              <textarea
                value={form.special_requests}
                onChange={update('special_requests')}
                disabled={submitting}
                rows={2}
                placeholder="Dietary needs, accessibility, questions, etc."
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-uif-green focus:ring-1 focus:outline-none"
              />
            </Field>

            <div className="flex gap-2 pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="flex-1 rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-uif-green text-white font-semibold px-4 py-2 hover:bg-uif-green-dark disabled:bg-gray-300"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {submitting ? 'Submitting…' : 'Reserve my seat'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-gray-700 mb-1">{label}</span>
      {children}
    </label>
  );
}
